"use client";

import { getLocation } from "@faire/web--source/common/globals/getLocation";
import { getWindow } from "@faire/web--source/common/globals/getWindow";
import { logError } from "@faire/web--source/common/logging";
import { appendFaireDirectBrandAliasQueryParam } from "@faire/web--source/common/routes/faire-direct/appendFaireDirectBrandAliasQueryParam";
import { RoutingContext } from "@faire/web--source/ui/routing/RoutingContext";
import type {
  IRoutingContext,
  ILinkComponentProps,
} from "@faire/web--source/ui/routing/types";
import {
  matchLocale,
  prependLocaleToUrl,
} from "@faire/web--source/ui/routing/util";
import * as H from "history";
import React from "react";
import {
  // eslint-disable-next-line @faire-web/no-restricted-imports
  Link as ReactLink,
  LinkProps as ReactLinkProps,
  useHistory,
} from "react-router-dom";

import { getShouldUseSoftNavigation } from "./routing/getShouldUseSoftNavigation";

export type NavigationType = "hard" | "soft" | "shallow";

export type ILinkProps<S = H.LocationState> = Omit<ReactLinkProps<S>, "to"> & {
  to: H.LocationDescriptor<S>;
  skipPrependLocale?: boolean;
  prefetch?: boolean;

  /* The type of navigation that will be triggered when the link is clicked
   * "hard" - the browser handles navigation natively (direct load)
   * "soft" - React Router's Link / NextLink handles navigation
   * "shallow" - pushState() updates the URL (without RSC fetch)
   *
   * If not specified, the default value is "soft".
   */
  nav?: NavigationType;
};

/**
 * Thin wrapper around React Links (from react-router-dom).
 * The thin wrapper allows us to globally augment all Faire URLs, if we want to.
 */
export const Link = <S extends H.LocationState = H.LocationState>(
  props: ILinkProps<S> & React.RefAttributes<HTMLAnchorElement>
) => {
  const routingContext = React.useContext(RoutingContext);
  const { Link = ReactLink, useRouterOrHistory = useHistory } = routingContext;
  const { to, skipPrependLocale, nav, onClick, ...otherProps } = props;
  const history = useRouterOrHistory();

  const { toStr, augmentedToStr, augmentedTo } = getAugmentedLocation({
    to,
    skipPrependLocale,
    routingContext,
  });

  if (nav === "hard") {
    // eslint-disable-next-line @faire/no-raw-anchor-tag
    return <a href={augmentedToStr} onClick={onClick} {...otherProps} />;
  } else {
    const linkProps: ILinkComponentProps = {
      innerRef: props.ref,
      to: augmentedTo,
      onClick: (e) => {
        onClick?.(e);

        if (!matchLocale(toStr, routingContext?.locale)) {
          history.push(augmentedToStr);
          getLocation()?.reload();
        }

        if (!getShouldUseSoftNavigation(nav, routingContext, augmentedToStr)) {
          e.preventDefault();
          getWindow()?.history.pushState(null, "", augmentedToStr);
        }
      },
      ...otherProps,
    };

    return <Link {...linkProps} />;
  }
};

type AnchorProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  prefetch?: boolean;
};

/**
 * Thin wrapper around `<a>` tags.
 * The thin wrapper allows us to globally augment all Faire URLs, if we want to.
 */
export const Anchor: React.FC<
  AnchorProps & React.RefAttributes<HTMLAnchorElement>
> = (props) => {
  const {
    faireDirectBrandAlias,
    faireDirectBrandToken,
    faireDirectWidgetToken,
    ...routingContext
  } = React.useContext(RoutingContext);
  const { children, href, ...otherProps } = props;

  const potentiallyPrependLocaleHref = href?.startsWith("/")
    ? prependLocaleToUrl(href, routingContext)
    : href;

  const shouldAppendFaireDirectQueryParam =
    faireDirectBrandAlias &&
    faireDirectBrandToken &&
    !!potentiallyPrependLocaleHref;

  const augmentedHref = shouldAppendFaireDirectQueryParam
    ? appendFaireDirectBrandAliasQueryParam({
        path: potentiallyPrependLocaleHref ?? "",
        faireDirectBrandAlias,
        faireDirectBrandToken,
        faireDirectWidgetToken,
      })
    : potentiallyPrependLocaleHref;

  return (
    // eslint-disable-next-line @faire/no-raw-anchor-tag
    <a ref={props.ref} href={augmentedHref} {...otherProps}>
      {children}
    </a>
  );
};

function getAugmentedLocation({
  to,
  skipPrependLocale,
  routingContext,
}: {
  to: H.LocationDescriptor<H.LocationState>;
  skipPrependLocale: boolean | undefined;
  routingContext: IRoutingContext;
}): {
  toStr: string;
  augmentedToStr: string;
  augmentedTo: H.LocationDescriptor<H.LocationState>;
} {
  const {
    faireDirectBrandAlias,
    faireDirectBrandToken,
    faireDirectWidgetToken,
  } = routingContext;

  if (to == null) {
    logError(
      new Error("LinkAnchor: 'to' is null: " + React.captureOwnerStack())
    );

    return {
      augmentedTo: "",
      toStr: "",
      augmentedToStr: "",
    };
  } else if (typeof to === "object") {
    const augmentedTo = to;
    augmentedTo.pathname = prependLocaleToUrl(`${to.pathname}`, routingContext);

    if (faireDirectBrandAlias && faireDirectBrandToken) {
      augmentedTo.search = appendFaireDirectBrandAliasQueryParam({
        path: augmentedTo.pathname,
        faireDirectBrandAlias,
        faireDirectBrandToken,
        faireDirectWidgetToken,
        search: augmentedTo.search ?? "",
      });
    }

    return {
      augmentedTo,
      toStr: to.pathname ?? "",
      augmentedToStr: augmentedTo.pathname ?? "",
    };
  } else {
    /**
     * for bots language selectors, we do not want to prependLocale to US links
     * for example, if localized path is /careers, we want to direct bots to /careers, not /en/careers
     */
    let augmentedTo = skipPrependLocale
      ? to
      : prependLocaleToUrl(`${to}`, routingContext);

    if (faireDirectBrandAlias && faireDirectBrandToken) {
      augmentedTo = appendFaireDirectBrandAliasQueryParam({
        path: augmentedTo,
        faireDirectBrandAlias,
        faireDirectBrandToken,
        faireDirectWidgetToken,
      });
    }

    return {
      augmentedTo,
      toStr: to,
      augmentedToStr: augmentedTo,
    };
  }
}
